import { useEffect, useRef, useState } from 'react';

import { useCurrentAsset } from '@/hooks/use-current-asset';

import { GetLevels } from '../api/queries/get-levels';
import { ListIds } from '../api/queries/list-ids';
import { type Domain } from '../components/filters/domain-toggle';
import { type BatteryLevel } from '../entities/battery-level';
import { type Filters } from '../entities/filters';

type UseAutoNavigateToDataProps = {
  filters: Filters;
  domain: Domain;
  levelsData: GetLevels.Output | undefined;
  onUpdateFilters: (partial: Partial<Filters>) => void;
  /** True when system-level chart data is confirmed empty */
  systemLevelDataEmpty: boolean;
  /** True when chart data queries are still loading */
  isLoadingChartData: boolean;
  /** True when URL has explicit params (user intentionally at this URL) */
  urlHasExplicitParams: boolean;
};

type AutoNavigateState = 'idle' | 'searching' | 'found' | 'not-found';

/**
 * Checks if any level filter is currently selected.
 * Returns true if user has made an explicit selection.
 */
function hasAnyLevelSelection(filters: Filters, domain: Domain): boolean {
  if (domain === 'battery') {
    const { arrayId, nodeId, containerId, bankId, packId, moduleId, cellId } =
      filters.batteryFilters;
    return Boolean(
      arrayId ||
      nodeId ||
      containerId ||
      bankId ||
      packId ||
      moduleId ||
      cellId,
    );
  }

  const { pcsId } = filters.pcsFilters;
  return Boolean(pcsId);
}

/**
 * Auto-navigates to the first hierarchy level with available data.
 *
 * When a user navigates to DEX v2 without any level filters selected,
 * AND system-level data is confirmed empty, this hook traverses the
 * hierarchy (array → node → container → ...) and auto-selects the
 * first ID at the first level that has data.
 *
 * This prevents showing an empty "No data available" state when
 * system-level data doesn't exist but lower-level data does.
 *
 * Key behaviors:
 * - Only triggers on initial page load (not when user clears filters)
 * - Only searches after confirming system-level data is empty
 * - Sites with system-level data remain on system level
 */
export function useAutoNavigateToData({
  filters,
  domain,
  levelsData,
  onUpdateFilters,
  systemLevelDataEmpty,
  isLoadingChartData,
  urlHasExplicitParams,
}: UseAutoNavigateToDataProps): {
  isSearching: boolean;
  searchingLevel: string | null;
} {
  const { accureId } = useCurrentAsset();
  const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
  const [state, setState] = useState<AutoNavigateState>('idle');
  const hasNavigatedRef = useRef(false);

  // Get available levels in hierarchy order
  const levelsOrder = levelsData?.[domain]
    ? (Object.keys(
        levelsData[domain] as Record<BatteryLevel, string>,
      ) as BatteryLevel[])
    : [];

  // Get the customer-friendly label for the current level being searched
  const currentLevelLabel = levelsOrder[currentLevelIndex]
    ? ((levelsData?.[domain] as Record<BatteryLevel, string> | undefined)?.[
        levelsOrder[currentLevelIndex]
      ] ?? levelsOrder[currentLevelIndex])
    : null;

  const hasSelection = hasAnyLevelSelection(filters, domain);

  // Determine if we should be searching
  // Only search when:
  // 1. Haven't navigated yet
  // 2. URL doesn't have explicit params (user didn't intentionally navigate here)
  // 3. No current level selection
  // 4. System-level data is confirmed empty (not still loading)
  // 5. Levels are available
  const shouldSearch =
    !hasNavigatedRef.current &&
    !urlHasExplicitParams &&
    !hasSelection &&
    !isLoadingChartData &&
    systemLevelDataEmpty &&
    accureId &&
    levelsOrder.length > 0;

  // Query for IDs at the current level
  const currentLevel = levelsOrder[currentLevelIndex];
  const idsQuery = ListIds.useQuery({
    level: currentLevel ?? 'array',
    parentId: shouldSearch ? accureId : undefined,
    retry: 1,
  });

  // Handle the sequential traversal and auto-navigation
  useEffect(() => {
    if (!shouldSearch || hasNavigatedRef.current) {
      setState('idle');
      return;
    }

    // Update searching state
    if (idsQuery.isLoading) {
      setState('searching');
      return;
    }

    // Check if we found IDs at this level
    if (idsQuery.data?.ids && idsQuery.data.ids.length > 0) {
      const firstNode = idsQuery.data.ids[0];
      hasNavigatedRef.current = true;
      setState('found');

      // Auto-select the first ID
      if (domain === 'battery') {
        onUpdateFilters({
          batteryFilters: {
            [`${currentLevel}Id`]: firstNode.id,
          },
        });
      } else {
        onUpdateFilters({
          pcsFilters: {
            pcsId: firstNode.id,
          },
        });
      }
      return;
    }

    // No IDs at this level and query completed - try next level
    if (!idsQuery.isLoading && !idsQuery.isError) {
      if (currentLevelIndex < levelsOrder.length - 1) {
        setCurrentLevelIndex((prev) => prev + 1);
      } else {
        // Exhausted all levels - no data anywhere
        setState('not-found');
        hasNavigatedRef.current = true;
      }
    }

    // Handle errors - skip to next level
    if (idsQuery.isError && currentLevelIndex < levelsOrder.length - 1) {
      setCurrentLevelIndex((prev) => prev + 1);
    }
  }, [
    shouldSearch,
    idsQuery.isLoading,
    idsQuery.isError,
    idsQuery.data?.ids,
    currentLevel,
    currentLevelIndex,
    levelsOrder.length,
    domain,
    onUpdateFilters,
  ]);

  // Reset when domain changes
  useEffect(() => {
    hasNavigatedRef.current = false;
    setCurrentLevelIndex(0);
    setState('idle');
  }, [domain]);

  // Return isSearching=true when we should search but haven't found/exhausted yet
  // This prevents flashing the "No data" state while the search is about to begin
  const isActivelySearching = Boolean(
    shouldSearch && state !== 'found' && state !== 'not-found',
  );

  return {
    isSearching: isActivelySearching,
    searchingLevel: isActivelySearching ? currentLevelLabel : null,
  };
}
